import moment from 'moment';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Box, Chip, Stack, Tooltip, Typography, IconButton } from '@mui/material';

import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/routes/paths';

import { updateAutomationStatus } from 'src/server/store/automation';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import TextMaxLine from 'src/components/text-max-line';
import { useSettingsContext } from 'src/components/settings';

// ----------------------------------------------------------------------

// Helper to convert 24-hour time to 12-hour format with AM/PM
export function formatTime12Hour(time24) {
  if (!time24) return '--';
  const [hourStr, minuteStr] = time24.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr;
  if (Number.isNaN(hour) || Number.isNaN(parseInt(minute, 10))) return '';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour %= 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minute} ${ampm}`;
}

export default function AutomationTableRowAutomation({
  row,
  selected,
  index,
  stickyColumns = [],
  onTestRun,
}) {
  const settings = useSettingsContext();
  const dispatch = useDispatch();
  const router = useRouter();
  // Map new data structure fields
  const {
    title, // ruleTitle
    campaigns, // array of { campaignId, name }
    automationRuleTemplate,
    schedule, // { frequency }
    executionType,
    isActive,
    nextExecutionDate,
    createdAt,
    _id,
  } = row;

  // Map campaigns to names
  const targetCampaigns = Array.isArray(campaigns) ? campaigns.map((c) => c.name) : [];

  // Status mapping
  const status = isActive ? 'Active' : 'Inactive';

  // Define left offsets for sticky columns (adjust as needed)
  const stickyOffsets = [0, 62, 200, 350, 500, 650, 800, 950, 1100, 1250, 1400, 1550, 1700];

  const handleStatusChange = () => {
    dispatch(updateAutomationStatus({ credentials: { id: row._id } }));
  };

  const handleEditRow = () => {
    router.push(paths.dashboard.automation_update(_id));
  };

  const handleTestRun = () => {
    if (onTestRun) onTestRun(_id);
  };

  const handleFrequencyText = schedule?.frequency
    ? schedule.frequency.charAt(0).toUpperCase() +
      schedule.frequency.slice(1).toLowerCase().replace(/_/g, ' ')
    : '--';

  function DaysFinder(dayOfWeek) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek % 7];
  }

  const getOrdinal = (num) => {
    switch (num) {
      case 1:
      case 21:
      case 31:
        return 'st';
      case 2:
      case 22:
        return 'nd';
      case 3:
      case 23:
        return 'rd';
      case 4:
        return 'th';
      default:
        return 'th';
    }
  };

  return (
    <TableRow hover selected={selected}>
      {/* # */}
      <TableCell
        sx={
          stickyColumns.includes(1)
            ? {
                position: 'sticky',
                left: stickyOffsets[0],
                zIndex: 99,
                backgroundColor: settings?.themeMode === 'light' ? '#ECF0F1' : '#333',
                width: 62,
              }
            : { width: 62 }
        }
      >
        <Typography variant="caption" fontWeight={400} color="#4D8FAC">
          {index + 1}
        </Typography>
      </TableCell>
      {/* Rule Title (title) */}
      <TableCell
        sx={
          stickyColumns.includes(2)
            ? {
                position: 'sticky',
                left: stickyOffsets[1],
                zIndex: 98,
                backgroundColor: settings?.themeMode === 'light' ? '#ECF0F1' : '#333',
              }
            : {}
        }
      >
        <Typography variant="caption" fontWeight={500} color="#4D8FAC">
          {title || '--'}
        </Typography>
      </TableCell>

      {/* Target Campaigns (campaigns) */}
      <TableCell
        sx={{
          position: 'sticky',
          left: stickyOffsets[2],
          zIndex: 97,
          backgroundColor: settings?.themeMode === 'light' ? '#ECF0F1' : '#333',
          minWidth: 150,
          maxWidth: 150,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {targetCampaigns.length > 0 ? (
            <Chip
              label={`${targetCampaigns?.length} Campaigns`}
              size="small"
              sx={{
                backgroundColor: settings?.themeMode === 'light' ? '#F5F7FA' : '#222',
                color: settings?.themeMode === 'light' ? '#1976d2' : '#90caf9',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: settings?.themeMode === 'light' ? '#F5F7FA' : '#222', // Remove hover background color
                },
              }}
            />
          ) : (
            <Typography variant="caption" fontWeight={400} color="#4D8FAC">
              --
            </Typography>
          )}
        </Stack>
      </TableCell>

      {/* Rule (description) */}
      <TableCell sx={{ minWidth: 200 }}>
        <TextMaxLine maxLine={1}>
          {automationRuleTemplate?._id ? (
            <Link
              to={`${paths.dashboard.rule_template_update(automationRuleTemplate._id)}`}
              style={{ textDecoration: 'none' }}
            >
              <Typography variant="caption" fontWeight={400} color="primary">
                {automationRuleTemplate?.title || '--'}
              </Typography>
            </Link>
          ) : (
            <Typography variant="caption" fontWeight={400}>
              {automationRuleTemplate?.title || '--'}
            </Typography>
          )}
        </TextMaxLine>
      </TableCell>

      {/* Frequency */}
      <TableCell sx={{ minWidth: 180 }}>
        {schedule ? (
          <Tooltip
            title={
              schedule.startDate || schedule.endDate ? (
                <>
                  <Box>
                    <CalendarTodayIcon
                      fontSize="inherit"
                      sx={{ mr: 0.5, verticalAlign: 'middle' }}
                    />
                    <b>Start:</b> {schedule.startDate || '--'}
                  </Box>
                  <Box>
                    <CalendarTodayIcon
                      fontSize="inherit"
                      sx={{ mr: 0.5, verticalAlign: 'middle' }}
                    />
                    <b>End:</b> {schedule.endDate || '--'}
                  </Box>
                </>
              ) : (
                ''
              )
            }
            arrow
            placement="top"
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: settings?.themeMode === 'light' ? '#F5F7FA' : '#222',
                borderRadius: 2,
                px: 1.5,
                py: 1,
                boxShadow: 1,
                width: 'fit-content',
              }}
            >
              <Chip
                label={handleFrequencyText || '--'}
                size="small"
                color="primary"
                sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
              />

              {schedule?.frequency === 'MONTHLY' && schedule?.monthlyMode === 'BY_DATE' && (
                <>
                  {schedule?.dayOfMonth && (
                    <Chip
                      label={`${schedule.dayOfMonth}${getOrdinal(schedule.dayOfMonth)} ` || ''}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        bgcolor: '#e8f5e9',
                        color: '#388e3c',
                        '&:hover': {
                          backgroundColor: '#fffde7',
                        },
                      }}
                    />
                  )}
                </>
              )}

              {schedule?.frequency === 'MONTHLY' && schedule?.monthlyMode === 'BY_WEEK' && (
                <>
                  {schedule?.weekOfMonth && (
                    <Chip
                      label={`${schedule.weekOfMonth}${getOrdinal(schedule.weekOfMonth)} Week`}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        bgcolor: '#e8f5e9',
                        color: '#388e3c',
                        '&:hover': {
                          backgroundColor: '#fffde7',
                        },
                      }}
                    />
                  )}
                </>
              )}

              {schedule?.monthlyMode !== 'BY_DATE' && DaysFinder(schedule.dayOfWeek) && (
                <Chip
                  label={DaysFinder(schedule.dayOfWeek) || ''}
                  size="small"
                  sx={{
                    fontWeight: 500,
                    bgcolor: '#e8f5e9',
                    color: '#388e3c',
                    '&:hover': {
                      backgroundColor: '#fffde7',
                    },
                  }}
                />
              )}

              <Stack direction="row" alignItems="center" spacing={0.5}>
                <AccessTimeIcon fontSize="small" sx={{ color: '#90caf9', fontSize: 16 }} />
                <Typography variant="caption" fontWeight={500} width={60}>
                  {schedule.time ? `${formatTime12Hour(schedule.time)}` : '--'}
                </Typography>
              </Stack>
              <Typography variant="caption" fontWeight={400} color="text.secondary">
                {schedule.timezone || '--'}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography variant="caption" fontWeight={400}>
            --
          </Typography>
        )}
      </TableCell>
      {/* Execution Type */}
      <TableCell>
        <Typography variant="caption" fontWeight={400}>
          {executionType || '--'}
        </Typography>
      </TableCell>
      {/* Status */}
      <TableCell>
        <Label
          variant="soft"
          color={status === 'Active' ? 'success' : 'default'}
          sx={{ textTransform: 'uppercase' }}
        >
          {status || '--'}
        </Label>
      </TableCell>
      {/* Next Run (nextExecutionDate) */}
      <TableCell>
        <Typography variant="caption" fontWeight={700} sx={{ display: 'flex', maxWidth: '80px' }}>
          {nextExecutionDate && status === 'Active'
            ? moment(nextExecutionDate).format('DD MMM YYYY HH:mm a')
            : '--'}
        </Typography>
      </TableCell>
      {/* Created At */}
      <TableCell>
        <Typography variant="caption" fontWeight={400}>
          {createdAt ? moment(createdAt).format('DD MMM YYYY HH:mm:ss a') : '--'}
        </Typography>
      </TableCell>
      {/* Actions */}
      <TableCell>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Test run">
            <IconButton color="info" onClick={handleTestRun} size="small">
              <Iconify icon="solar:play-bold" />
            </IconButton>
          </Tooltip>
          <IconButton color="primary" onClick={handleEditRow} size="small">
            <Iconify icon="eva:edit-2-outline" />
          </IconButton>
          <Tooltip title={isActive ? 'Deactivate' : 'Activate'}>
            <IconButton
              color={isActive ? 'success' : 'default'}
              onClick={handleStatusChange}
              size="small"
            >
              <Iconify icon={isActive ? 'eva:toggle-right-fill' : 'eva:toggle-left-outline'} />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

AutomationTableRowAutomation.propTypes = {
  row: PropTypes.object,
  selected: PropTypes.bool,
  index: PropTypes.any,
  stickyColumns: PropTypes.array,
  onTestRun: PropTypes.func,
};
